import { computed, effect, Injectable, signal } from '@angular/core';
import type { Theme } from '../models/types';

const STORAGE_KEY = 'ur-workbench.theme';

type ThemeColor = {
  hex: string;
  light: string;
  dark: string;
  rgb: string;
};

const THEME_COLORS: Record<Theme, ThemeColor> = {
  light: { hex: '#22395f', light: '#6f86ac', dark: '#16253e', rgb: '34 57 95' },
  dark: { hex: '#33507f', light: '#7b93b5', dark: '#213556', rgb: '51 80 127' },
  ai: { hex: '#6366f1', light: '#9da0f8', dark: '#4338ca', rgb: '99 102 241' },
  system: { hex: '#22395f', light: '#6f86ac', dark: '#16253e', rgb: '34 57 95' },
};

/** Manages the active UI theme and persists user preference. */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly theme = signal<Theme>(this.readStored());
  readonly primaryColor = computed(() => THEME_COLORS[this.effective()]);

  /** The theme actually applied to the document (system is resolved). */
  readonly effective = computed<Exclude<Theme, 'system'>>(() => {
    const t = this.theme();
    if (t === 'system') {
      return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return t;
  });

  private readonly systemListener = () => {
    if (this.theme() === 'system') {
      this.applyAttribute();
    }
  };

  constructor() {
    effect(() => {
      const theme = this.theme();
      try {
        localStorage.setItem(STORAGE_KEY, theme);
      } catch {
        /* storage unavailable */
      }
      this.applyAttribute();
    });

    if (typeof window !== 'undefined') {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this.systemListener);
    }
  }

  setTheme(theme: Theme): void {
    this.theme.set(theme);
  }

  isDark(): boolean {
    return this.effective() !== 'light';
  }

  logoColorMode(): 'dynamic' | 'static' {
    return 'dynamic';
  }

  /** Cycles light → dark → ai → system. */
  cycle(): Theme {
    const order: Theme[] = ['light', 'dark', 'ai', 'system'];
    const next = order[(order.indexOf(this.theme()) + 1) % order.length];
    this.setTheme(next);
    return next;
  }

  private readStored(): Theme {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const valid: Theme[] = ['light', 'dark', 'ai', 'system'];
      return valid.includes(stored as Theme) ? (stored as Theme) : 'system';
    } catch {
      return 'system';
    }
  }

  private applyAttribute(): void {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', this.effective());
    }
  }
}
